import { isBefore } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";

import { ExistingDestination } from "@prequel/react";
import {
  Button,
  ButtonStyle,
  Checkbox,
  DateTimePicker,
  Modal,
  SectionExpandable,
  Spinner,
} from "@prequel/shared-components";

import TagsBuilder from "./TagsBuilder";

import { useDestinationEnabledModels } from "@/api/queries/destinations";
import { usePostTransfer } from "@/api/queries/transfers";
import { formatAsUTC } from "@/types";

const CONFIRMATION_TEXT = "delete all data";

type SyncSpecificModelsModalProps = {
  destination: ExistingDestination;
  onConfirm: () => void;
  onCancel: () => void;
  fullRefresh: boolean;
  initialSelectedModels?: string[];
  initialStartSyncTime?: string;
  initialEndSyncTime?: string;
  initialTags?: Record<string, string>;
};

const SyncSpecificModelsModal = ({
  destination,
  onConfirm,
  onCancel,
  fullRefresh,
  initialSelectedModels = [],
  initialStartSyncTime,
  initialEndSyncTime,
  initialTags,
}: SyncSpecificModelsModalProps) => {
  const { data: fetchedModels } = useDestinationEnabledModels(destination.id);
  const postTransferMutation = usePostTransfer();
  const [useSpecificStartTime, setUseSpecificStartTime] = useState(!!initialStartSyncTime);
  const [useSpecificEndTime, setUseSpecificEndTime] = useState(!!initialEndSyncTime);
  const [startSyncTime, setStartSyncTime] = useState(() => {
    if (!initialStartSyncTime) {
      return "";
    }
    try {
      return new Date(initialStartSyncTime).toISOString().slice(0, 16);
    } catch {
      return "";
    }
  });
  const [endSyncTime, setEndSyncTime] = useState(() => {
    if (!initialEndSyncTime) {
      return "";
    }
    try {
      return new Date(initialEndSyncTime).toISOString().slice(0, 16);
    } catch {
      return "";
    }
  });
  const [tagsError, setTagsError] = useState<string>();
  const [tags, setTags] = useState<Array<{ key: string; value: string }>>(() => {
    if (initialTags && Object.keys(initialTags).length > 0) {
      return Object.entries(initialTags).map(([key, value]) => ({
        key,
        value,
      }));
    }
    return [{ key: "", value: "" }];
  });

  const sortedModels = useMemo(
    () => [...(fetchedModels ?? [])].sort((a, b) => a.model_name.localeCompare(b.model_name)),
    [fetchedModels],
  );

  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [confirmationText, setConfirmationText] = useState("");
  const isInitialized = useRef(false);

  const handleCancel = () => {
    setConfirmationText("");
    onCancel();
  };

  useEffect(() => {
    // Only initialize once when enabled models are loaded
    if (sortedModels.length > 0 && !isInitialized.current) {
      const enabledModelNames = sortedModels.map((m) => m.model_name);
      if (initialSelectedModels && initialSelectedModels.length > 0) {
        setSelectedModels(initialSelectedModels.filter((m) => enabledModelNames.includes(m)));
      }
      isInitialized.current = true;
    }
  }, [sortedModels, initialSelectedModels]);

  const allSelected = sortedModels.length > 0 && selectedModels.length === sortedModels.length;

  const toggleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      setSelectedModels(sortedModels.map((m) => m.model_name));
    } else {
      setSelectedModels([]);
    }
  };

  const updateSelectedModels = (isEnabled: boolean, modelName: string) => {
    const updatedModels = isEnabled
      ? selectedModels.filter((m) => m !== modelName).concat([modelName])
      : selectedModels.filter((m) => m !== modelName);

    setSelectedModels(updatedModels);
  };

  const startTimeValid = useMemo(() => {
    const startUTC = formatAsUTC(startSyncTime);

    if (useSpecificStartTime && !startUTC) {
      return false;
    }

    return true;
  }, [useSpecificStartTime, startSyncTime]);

  const endTimeValid = useMemo(() => {
    const endUTC = formatAsUTC(endSyncTime);

    if (useSpecificEndTime && !endUTC) {
      return false;
    }

    return true;
  }, [useSpecificEndTime, endSyncTime]);

  const syncTimesValid = useMemo(() => {
    const startUTC = formatAsUTC(startSyncTime);
    const endUTC = formatAsUTC(endSyncTime);

    if (!useSpecificStartTime || !useSpecificEndTime || !startUTC || !endUTC) {
      // If either time is undefined or unused, times are valid
      return true;
    }

    return isBefore(startUTC, endUTC);
  }, [useSpecificStartTime, startSyncTime, useSpecificEndTime, endSyncTime]);

  const tagsValid = () => {
    const allowed = /^[a-zA-Z0-9_-]+$/;
    for (const { key, value } of tags) {
      // Both empty is allowed
      if (!key && !value) {
        continue;
      }
      // If only one is empty
      if (!key || !value) {
        return "Each tag must have both a key and a value, or both blank.";
      }
      // Check allowed characters
      if (!allowed.test(key) || !allowed.test(value)) {
        return "Keys and values must be alphanumeric, underscores, or dashes.";
      }
    }
    return undefined;
  };

  const onConfirmSyncSelected = () => {
    const errMsg = tagsValid();
    if (errMsg) {
      setTagsError(errMsg);
      return;
    } else {
      setTagsError(undefined);
    }
    // Convert tags array to record, filtering out empty tags
    const tagsRecord =
      tags.length > 0
        ? tags.reduce(
            (acc, tag) => {
              if (tag.key && tag.value) {
                acc[tag.key] = tag.value;
              }
              return acc;
            },
            {} as Record<string, string>,
          )
        : undefined;

    postTransferMutation.mutate({
      destinationID: destination.id,
      fullRefresh: fullRefresh,
      models: selectedModels,
      startTransferWindowAt: useSpecificStartTime ? formatAsUTC(startSyncTime) : undefined,
      endTransferWindowAt: useSpecificEndTime ? formatAsUTC(endSyncTime) : undefined,
      tags: tagsRecord,
    });
    setConfirmationText("");
    onConfirm();
  };

  return (
    <div className="space-y-4">
      <div className="sm:flex sm:items-start">
        <div className="text-left">
          <Modal.Title as="h3" className="text-lg/6 font-medium text-gray-900">
            {fullRefresh ? "Select models to full refresh" : "Select models to sync"}
          </Modal.Title>
        </div>
      </div>
      {sortedModels.length > 0 ? (
        <>
          <div className="h-px w-full bg-gray-200" />
          <Checkbox id="select-all-models" label="Select all" checked={allSelected} setChecked={toggleSelectAll} />
          <div className="h-px w-full bg-gray-200" />
          <div className="space-y-2">
            {sortedModels.map((model) => (
              <Checkbox
                key={model.model_name}
                id={model.model_name}
                label={model.model_name}
                checked={selectedModels.includes(model.model_name)}
                setChecked={(isChecked: boolean) => updateSelectedModels(isChecked, model.model_name)}
              />
            ))}
          </div>
        </>
      ) : (
        <Spinner />
      )}
      {fullRefresh && (
        <div className="-mb-2 flex-col rounded-sm border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
          <div className="flex">
            <div className="ml-3">
              <p className="mb-1 font-medium text-red-700">
                Are you sure you want to force a full data refresh for these selected models?
              </p>
              <p>
                {`This will delete all existing data for these models in the destination and re-sync all available data. This process may take a while.`}
              </p>
              <p className="mt-3">
                Please type <span className="font-semibold text-gray-700">{CONFIRMATION_TEXT}</span> to confirm.
              </p>
              <input
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                className="
                  mt-2 block w-full rounded-md border-gray-300 shadow-sm
                  focus:border-primary-500 focus:ring-primary-500
                  sm:text-sm
                "
                placeholder={CONFIRMATION_TEXT}
              />
            </div>
          </div>
        </div>
      )}
      <div className="h-px w-full bg-gray-200" /> {/* Divider  */}
      <SectionExpandable
        label="Advanced options"
        defaultOpen={
          !!initialStartSyncTime || !!initialEndSyncTime || (initialTags && Object.keys(initialTags).length > 0)
        }
      >
        <div className="space-y-6">
          <div>
            <Checkbox
              id="use-start-time"
              label="Use specific start time"
              checked={useSpecificStartTime}
              setChecked={setUseSpecificStartTime}
            />
            <DateTimePicker
              className="mt-2"
              label="Start from"
              dateTime={startSyncTime}
              setDateTime={setStartSyncTime}
              disabled={!useSpecificStartTime}
            />
            <p className="mt-2 text-xs font-medium text-gray-400">
              If unspecified, the transfer will sync data starting from the greatest{" "}
              <span className="text-mono rounded-sm bg-gray-50 px-1 font-normal">updated_at</span> timestamp that was
              successfully synced to this destination (per model).
            </p>
          </div>
          <div>
            <Checkbox
              id="use-end-time"
              label="Use specific end time"
              checked={useSpecificEndTime}
              setChecked={setUseSpecificEndTime}
            />
            <DateTimePicker
              className="mt-2"
              label="End at"
              dateTime={endSyncTime}
              setDateTime={setEndSyncTime}
              disabled={!useSpecificEndTime}
            />
            <p className="mt-2 text-xs font-medium text-gray-400">
              If unspecified, the transfer will sync data without an upper bound.
            </p>
          </div>
          {!syncTimesValid && (
            <p className="mt-1 text-xs font-medium text-red-600">Start time must be before end time.</p>
          )}
          <TagsBuilder tags={tags} setTags={setTags} errorMsg={tagsError} />
        </div>
      </SectionExpandable>
      <div className="h-px w-full bg-gray-200" /> {/* Divider  */}
      <div className="space-x-3">
        <Button
          text={fullRefresh ? "Full Refresh Models" : "Sync Models"}
          disabled={
            selectedModels.length === 0 ||
            !syncTimesValid ||
            !startTimeValid ||
            !endTimeValid ||
            (fullRefresh && confirmationText !== CONFIRMATION_TEXT)
          }
          type={fullRefresh ? ButtonStyle.DANGER : ButtonStyle.SECONDARY}
          onClick={onConfirmSyncSelected}
        />
        <Button text="Cancel" type={ButtonStyle.TERTIARY} onClick={handleCancel} />
      </div>
    </div>
  );
};

export default SyncSpecificModelsModal;
